import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useIdeas } from '../hooks/useIdeas';
import LoadingSpinner from '../components/common/LoadingSpinner';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import AnimatedBackground from '../components/AnimatedBackground';
import IdeaGeneratorWizard from './components/IdeaGeneratorWizard';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/Card';
import Button from '../components/ui/Button';
import { SkeletonCard } from '../components/ui/LoadingSkeleton';
import { EmptyBusinessIdeas } from '../components/ui/EmptyState';
import PricingSection from '../components/Home/PricingSection';
import { 
  BusinessIdea as BusinessIdeaType,
  BusinessIdeaGenerationParams,
  SkillLevel,
  SkillWithLevel,
  MarketScope
} from '../services/businessIdeaTypes';
import { TimeCommitmentNode } from '../data/timeCommitmentTaxonomy';
import { WorkEnvironmentNode } from '../data/workEnvironmentTaxonomy';
import { generateBusinessIdeas } from '../services/openaiService';
import { supabaseHelper } from '../lib/supabase';
import { updateMetaTags } from '../utils/seo';

interface GeneratedIdea extends BusinessIdeaType {
  name: string;
  products?: Array<{
    name: string;
    description: string;
    estimatedCost?: number;
  }>;
  services?: Array<{
    name: string;
    description: string;
    estimatedPrice?: number;
  }>;
  requiredSkills: Array<{
    name: string;
    userLevel?: SkillLevel | 'none';
    skillGap?: 'none' | 'missing';
  }>;
  timeCommitment: string;
  workEnvironment: string;
}

const IdeaGenerator: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { createIdea } = useIdeas();
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generatedIdeas, setGeneratedIdeas] = useState<GeneratedIdea[]>([]);
  const [showWizard, setShowWizard] = useState(true);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const [showTrialOffer, setShowTrialOffer] = useState(false);
  const [prefill, setPrefill] = useState<{
    skills?: Array<{ id: string; level?: SkillLevel } | string>;
    timeCommitmentId?: string;
    workEnvironmentIds?: string[];
    marketScope?: MarketScope;
  } | undefined>(undefined);

  // Simple analytics helper (GA4 if available)
  const track = (event: string, params: Record<string, unknown> = {}) => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const w = window as any;
      if (w && typeof w.gtag === 'function') {
        w.gtag('event', event, params);
      }
    } catch {}
    // Always log for debugging visibility
    // console.log(`[analytics] ${event}`, params);
  };

  // SEO meta + initial view event
  useEffect(() => {
    const url = typeof window !== 'undefined' ? window.location.href : 'https://explanatum.co.uk/idea-generator';
    updateMetaTags({
      title: 'Business Idea Generator | Explanatum',
      description: 'Generate tailored business ideas based on your skills, time, budget, and location preferences.',
      ogTitle: 'Business Idea Generator | Explanatum',
      ogDescription: 'Turn your skills and constraints into real business ideas in minutes.',
      ogUrl: url,
      twitterUrl: url,
      ogImage: 'https://explanatum.co.uk/og-image.jpg',
      canonicalUrl: 'https://explanatum.co.uk/idea-generator'
    });
    track('view_idea_generator');
    // parse query params for prefill
    try {
      const params = new URL(url).searchParams;
      const skills = params.get('skills');
      const time = params.get('time') || params.get('timeCommitmentId');
      const work = params.get('work') || params.get('workEnvironmentIds');
      const scope = params.get('scope') as MarketScope | null;
      const pf: typeof prefill = {};
      if (skills) {
        pf.skills = skills.split(',').map(s => s.trim()).filter(Boolean);
      }
      if (time) pf.timeCommitmentId = time;
      if (work) pf.workEnvironmentIds = work.split(',').map(w => w.trim()).filter(Boolean);
      if (scope && (['local','national','international'] as const).includes(scope)) pf.marketScope = scope;
      if (pf.skills || pf.timeCommitmentId || pf.workEnvironmentIds || pf.marketScope) {
        setPrefill(pf);
        track('prefill_detected', {
          skills_count: pf.skills?.length || 0,
          has_time: !!pf.timeCommitmentId,
          has_work: !!pf.workEnvironmentIds?.length,
          scope: pf.marketScope || 'none'
        });
      }
    } catch {}
  }, []);

  // Helper: limit free generation to 1/day for guests
  const getTodayKey = () => new Date().toISOString().slice(0, 10);
  const FREE_LIMIT_KEY = 'freeIdeaLastRun';
  const canGenerateToday = () => {
    if (user) return true; // authenticated users proceed (premium gating handled elsewhere)
    try {
      const last = localStorage.getItem(FREE_LIMIT_KEY);
      return last !== getTodayKey();
    } catch {
      return true;
    }
  };

  // Initialize state with default values to prevent undefined errors
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [selectedSkills, setSelectedSkills] = useState<SkillWithLevel[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [timeCommitment, setTimeCommitment] = useState<TimeCommitmentNode | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [workEnvironment, setWorkEnvironment] = useState<WorkEnvironmentNode[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [marketScope, setMarketScope] = useState<MarketScope>('local'); // Default to 'local'

  const handleWizardComplete = (data: {
    skills: SkillWithLevel[];
    timeCommitment: TimeCommitmentNode;
    workEnvironment: WorkEnvironmentNode[];
    marketScope: MarketScope;
  }) => {
    console.log('Wizard Complete Data:', {
      skills: data.skills,
      timeCommitment: data.timeCommitment,
      workEnvironment: data.workEnvironment,
      marketScope: data.marketScope
    });

    // Comprehensive validation with detailed logging
    const validationErrors: string[] = [];

    if (!data.skills || data.skills.length === 0) {
      console.error('Validation Error: No skills selected');
      validationErrors.push('Please select at least one skill');
    }

    if (!data.timeCommitment) {
      console.error('Validation Error: No time commitment selected');
      validationErrors.push('Please select a time commitment');
    }

    if (!data.workEnvironment || data.workEnvironment.length === 0) {
      console.error('Validation Error: No work environment selected');
      validationErrors.push('Please select at least one work environment');
    }

    if (!data.marketScope) {
      console.error('Validation Error: No market scope selected');
      validationErrors.push('Please select a market scope');
    }

    // If there are validation errors, show them and keep wizard open
    if (validationErrors.length > 0) {
      setError(validationErrors.join('. '));
      setShowWizard(true);
      return;
    }

    // Free tier throttle: 1 idea/day for guests
    if (!canGenerateToday()) {
      setShowTrialOffer(true);
      track('limit_reached_free_idea');
      return;
    }

    // Clear any previous errors
    setError(null);

    // Set all state values
    setSelectedSkills(data.skills);
    setTimeCommitment(data.timeCommitment);
    setWorkEnvironment(data.workEnvironment);
    setMarketScope(data.marketScope);
    track('wizard_complete', {
      skills_count: data.skills?.length || 0,
      time_commitment: data.timeCommitment?.name,
      work_environment_count: data.workEnvironment?.length || 0,
      market_scope: data.marketScope
    });
    
    // Hide wizard and start generating ideas
    setShowWizard(false);
    setIsGenerating(true);
    
    // Use a timeout to ensure state updates before generating ideas
    setTimeout(() => {
      handleGenerateIdeas(
        data.skills, 
        data.timeCommitment, 
        data.workEnvironment, 
        data.marketScope
      );
    }, 100);
  };

  const handleGenerateIdeas = async (
    skills: SkillWithLevel[], 
    commitment: TimeCommitmentNode, 
    environment: WorkEnvironmentNode[], 
    scope: MarketScope
  ) => {
    console.log('Wizard Complete Inputs:', {
      skills,
      commitment,
      environment,
      scope
    });

    const validationErrors: string[] = [];

    if (!skills || skills.length === 0) {
      console.error('Generate Ideas Error: No skills');
      validationErrors.push('Skills are missing');
    }

    if (!commitment) {
      console.error('Generate Ideas Error: No time commitment');
      validationErrors.push('Time commitment is missing');
    }

    if (!environment || environment.length === 0) {
      console.error('Generate Ideas Error: No work environment');
      validationErrors.push('Work environment is missing');
    }

    if (!scope) {
      console.error('Generate Ideas Error: No market scope');
      validationErrors.push('Market scope is missing');
    }

    // If there are validation errors, show them and reopen wizard
    if (validationErrors.length > 0) {
      setError(validationErrors.join('. '));
      setShowWizard(true);
      setIsGenerating(false);
      return;
    }

    const params: BusinessIdeaGenerationParams = {
      skills: skills.map(skill => skill.id),
      timeCommitment: commitment.name,
      workEnvironment: environment.map(env => env.name).join(', '),
      marketScope: scope
    };

    console.log('Generating Ideas - Params:', params);
    track('generate_idea', {
      skills_count: skills.length,
      time_commitment: commitment?.name,
      work_environment_count: environment.length,
      market_scope: scope
    });

    try {
      setIsGenerating(true);
      setError(null);
      
      const ideas = await generateBusinessIdeas(params);
      
      console.log('Generated Ideas:', ideas);

      if (!ideas || ideas.length === 0) {
        setError('No business ideas could be generated. Please try again.');
        setShowWizard(true);
      } else {
        setGeneratedIdeas(ideas as GeneratedIdea[]);
        track('view_idea_results', { ideas_count: ideas.length });
        // Mark today's free usage for guests
        if (!user) {
          try { localStorage.setItem(FREE_LIMIT_KEY, getTodayKey()); } catch {}
        }
      }
    } catch (err) {
      console.error('Detailed idea generation error:', err);
      setError(err instanceof Error ? err.message : 'Failed to generate ideas. Please try again.');
      setShowWizard(true);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSaveIdea = async (idea: GeneratedIdea) => {
    if (!user) {
      setShowAuthPrompt(true);
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setSaveSuccess(null);
      
      const savedIdea = await supabaseHelper.createIdea({
        title: idea.name,
        description: idea.description,
        target_market: idea.industry,
        industry: idea.industry,
        revenue_model: JSON.stringify(idea.revenueModel || {
          primaryRevenueSources: [],
          pricingStrategy: 'Basic',
          monthlyPricing: {
            basePrice: 0,
            additionalRevenueSources: 0
          },
          annualProjection: {
            year1: 0,
            year3: 0,
            year5: 0
          }
        }),
        products: idea.products,
        services: idea.services,
        details: {
          products: idea.products,
          services: idea.services
        }
      });
      
      setSaveSuccess(`Idea "${savedIdea.title}" saved successfully!`);
      setTimeout(() => setSaveSuccess(null), 3000);
    } catch (error) {
      console.error('Failed to save idea:', error);
      setError('Failed to save idea. Please try again.');
    } finally {
      setSaving(false);
    }
  };
  return (
    <section className="min-h-screen flex flex-col relative overflow-hidden">
      {/* Background with gradient and mesh */}
      <div className="absolute inset-0 bg-gray-50 z-0">
        <div className="absolute inset-0 opacity-30 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgdmlld0JveD0iMCAwIDYwIDYwIiBmaWxsPSJub25lIj48cGF0aCBkPSJNLjUuNWg2MHY2MEguNVYuNXptMzAgMTVhMTUgMTUgMCAwIDAtMTUgMTUgMTUgMTUgMCAwIDAgMTUgMTUgMTUgMTUgMCAwIDAgMTUtMTUgMTUgMTUgMCAwIDAtMTUtMTV6bTAgMGExNSAxNSAwIDAgMC0xNSAxNSAxNSAxNSAwIDAgMCAxNSAxNSAxNSAxNSAwIDAgMCAxNS0xNSAxNSAxNSAwIDAgMC0xNS0xNXoiIHN0cm9rZT0iIzIwMjAyMCIgc3Ryb2tlLW9wYWNpdHk9Ii4wNiIvPjwvc3ZnPg=='))] bg-[size:50px_50px]"></div>
      </div>

      {/* Decorative blobs */}
      <div className="absolute top-20 left-12 w-64 h-64 bg-purple-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-4000"></div>
      <div className="absolute -bottom-32 right-12 w-64 h-64 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
      <div className="absolute top-1/3 left-1/3 w-64 h-64 bg-pink-200 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob animation-delay-2000"></div>

      <div className="container relative z-10 mx-auto px-4 py-8 flex-grow">
        {showWizard ? (
          <IdeaGeneratorWizard
            onComplete={handleWizardComplete}
            prefill={prefill}
            onCancel={() => {
              setShowWizard(false);
              navigate('/');
            }}
          />
        ) : isGenerating ? (
          <div className="space-y-6 mt-12">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Generating Your Ideas...</h2>
              <p className="text-gray-600">Our AI is analyzing your inputs to create personalized business ideas</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {Array.from({ length: 4 }).map((_, i) => (
                <SkeletonCard key={i} />
              ))}
            </div>
          </div>
        ) : generatedIdeas.length > 0 ? (
          <div className="space-y-6 mt-4">
            <div className="relative mb-10 text-center">
              <h2 className="text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 inline-block">
                Generated Business Ideas
              </h2>
              <div className="h-1 w-48 mx-auto rounded-full bg-gradient-to-r from-blue-400 to-purple-500 mt-2"></div>
            </div>

            {/* Gated premium CTAs */}
            <div className="relative overflow-hidden rounded-xl bg-white/30 backdrop-blur-lg border border-white/40 p-5">
              <div className="absolute inset-0 bg-gradient-to-br from-yellow-200/20 to-pink-200/10"></div>
              <div className="relative z-10 grid gap-4 md:grid-cols-3">
                <button
                  onClick={() => {
                    track('click_trial_cta_business_plan', { location: 'idea_results_top' });
                    navigate('/upgrade?src=idea_generator&cta=business_plan');
                  }}
                  className="px-4 py-3 rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-medium shadow hover:shadow-lg transition"
                >
                  Generate Business Plan (7-day trial)
                </button>
                <button
                  onClick={() => {
                    track('click_trial_cta_logo', { location: 'idea_results_top' });
                    navigate('/upgrade?src=idea_generator&cta=logo');
                  }}
                  className="px-4 py-3 rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 text-white font-medium shadow hover:shadow-lg transition"
                >
                  Get Logo Concepts (7-day trial)
                </button>
                <button
                  onClick={() => {
                    track('click_trial_cta_consultant', { location: 'idea_results_top' });
                    navigate('/upgrade?src=idea_generator&cta=consultant');
                  }}
                  className="px-4 py-3 rounded-lg bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-medium shadow hover:shadow-lg transition"
                >
                  Chat with AI Business Consultant (7-day trial)
                </button>
              </div>
            </div>

            {generatedIdeas.map((idea, index) => (
              <Card key={index} variant="elevated" hoverable className="relative">
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <CardTitle className="text-xl bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-indigo-600">
                        {idea.name}
                      </CardTitle>
                      <CardDescription className="mt-2 text-base">{idea.description}</CardDescription>
                    </div>
                    <Button
                      variant="success"
                      size="sm"
                      onClick={() => handleSaveIdea(idea)}
                      disabled={saving}
                      loading={saving}
                      icon={saving ? undefined : '💾'}
                      className="ml-4"
                    >
                      {saving ? 'Saving...' : 'Save'}
                    </Button>
                  </div>
                </CardHeader>

                <CardContent>
                  {/* Additional idea details */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {idea.industry && (
                      <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                        <span className="font-semibold text-gray-700">Industry:</span>
                        <span className="ml-2 text-gray-600">{idea.industry}</span>
                      </div>
                    )}
                    {idea.targetMarket && (
                      <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                        <span className="font-semibold text-gray-700">Target Market:</span>
                        <span className="ml-2 text-gray-600">{idea.targetMarket}</span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}

            <div className="flex justify-center mt-8">
              <Button
                variant="primary"
                size="lg"
                onClick={() => setShowWizard(true)}
                icon="✨"
              >
                Generate New Ideas
              </Button>
            </div>
          </div>
        ) : (
          <div className="text-center py-12 relative overflow-hidden rounded-xl bg-white/20 backdrop-blur-lg border border-white/30 shadow-lg mt-12">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-400 to-indigo-500 opacity-5"></div>
            <div className="relative z-10 p-6">
              <h2 className="text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 mb-4">
                Business Idea Generator
              </h2>
              <p className="text-xl text-gray-700 mb-8 max-w-2xl mx-auto">
                Ready to explore business ideas tailored to your skills? Let's get started!
              </p>
              <button
                onClick={() => {
                  track('click_start_generator');
                  setShowWizard(true);
                }}
                className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg shadow-lg hover:shadow-blue-500/30 transition-all duration-300 transform hover:-translate-y-0.5 font-medium"
              >
                Start Idea Generator
              </button>
            </div>
          </div>
        )}

        {/* Authentication Prompt Modal */}
        {showAuthPrompt && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <div className="relative overflow-hidden rounded-xl bg-white/90 backdrop-blur-lg border border-white/50 shadow-xl p-6 max-w-md w-full">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-400/10 to-purple-500/10"></div>
              <div className="relative z-10">
                <h3 className="text-xl font-semibold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">Save Your Ideas</h3>
                <p className="text-gray-700 mb-6">
                  Create an account to save your business ideas and access additional features like the Business Plan Generator!
                </p>
                <div className="flex justify-end space-x-4">
                  <button
                    onClick={() => setShowAuthPrompt(false)}
                    className="px-4 py-2 bg-white/80 backdrop-blur-sm text-gray-700 rounded-lg hover:bg-white transition-colors border border-gray-200"
                  >
                    Maybe Later
                  </button>
                  <Link
                    to="/signup"
                    className="px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg shadow-md hover:shadow-blue-500/30 transition-all duration-300"
                  >
                    Sign Up Now
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Free daily limit reached -> offer 7-day trial */}
        {showTrialOffer && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <div className="relative overflow-hidden rounded-xl bg-white/95 backdrop-blur-lg border border-white/60 shadow-2xl p-0 max-w-3xl w-full">
              <div className="absolute inset-0 bg-gradient-to-br from-indigo-400/10 to-purple-500/10"></div>
              <div className="relative z-10">
                <div className="px-6 pt-6">
                  <h3 className="text-2xl font-semibold text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600">
                    Daily free idea used
                  </h3>
                  <p className="text-gray-700 mt-2 mb-4">
                    Enjoy unlimited generation plus Business Plan, Logo Concepts, AI Consultant and more with a 7-day free trial.
                  </p>
                  <div className="flex justify-end">
                    <button
                      onClick={() => setShowTrialOffer(false)}
                      className="px-3 py-2 text-sm bg-white/80 backdrop-blur-sm text-gray-700 rounded-lg hover:bg-white transition-colors border border-gray-200"
                    >
                      Maybe Later
                    </button>
                  </div>
                </div>
                <div className="border-t border-gray-200/70">
                  <PricingSection />
                </div>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="fixed inset-x-0 bottom-0 bg-red-100/90 backdrop-blur-sm border-t border-red-300 text-red-700 p-4 shadow-lg z-40">
            <div className="container mx-auto flex justify-between items-center">
              <p className="flex items-center">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                {error}
              </p>
              <div className="space-x-2 flex">
                <button
                  onClick={() => {
                    setError(null);
                    setShowWizard(true);
                  }}
                  className="px-4 py-2 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg shadow-sm hover:shadow-red-500/30 transition-all duration-300"
                >
                  Try Again
                </button>
                <button
                  onClick={() => setError(null)}
                  className="px-4 py-2 bg-white/80 backdrop-blur-sm text-gray-700 rounded-lg hover:bg-white transition-colors border border-gray-200"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        )}

        {saveSuccess && (
          <div className="fixed inset-x-0 bottom-0 bg-green-100/90 backdrop-blur-sm border-t border-green-300 text-green-700 p-4 shadow-lg z-40">
            <div className="container mx-auto flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <p>{saveSuccess}</p>
            </div>
          </div>
        )}
        {/* Unified 7‑day free trial signup (showcase features) */}
        <section aria-labelledby="free-trial" id="free-trial" className="mt-14">
          <PricingSection />
        </section>
      </div>
    </section>
  );
};

export default IdeaGenerator;
